import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';

/**
 * PricingComputation — calls the pricing-schema resolver for the given
 * schema and contract/estimate context, then renders one row per
 * component showing the resolved % rate, the value applied to the base,
 * and a flag when no master was found.
 *
 * Base value priority (per product spec):
 *   1. negotiatedValue   (preferred)
 *   2. totalValue        (fallback)
 *   3. user-entered      (modal prompt if both are zero/blank)
 *
 * Component category handling — these don't need a rate master:
 *   - 'base'      → value = base value (BASE component IS the base)
 *   - 'round_off' → value = base + sum(taxes/markups/levies) − sum(discounts)
 *
 * Anything else without a master → 0% with the red "no master" warning.
 *
 * The fetch effect refreshes whenever the schema, contract jurisdiction,
 * negotiated value, or total value changes.
 *
 * Props:
 *   schemaId               — UUID of the pricing schema to apply
 *   contractId             — optional; resolver picks customer/date from it
 *   estimateId             — optional alternative
 *   jurisdictionRegionId   — optional override for the L1 jurisdiction key.
 *                            When set, sent to the resolver so the page
 *                            re-resolves whenever the user changes it on the
 *                            contract header (no save required).
 *   negotiatedValue        — preferred base
 *   totalValue             — fallback base
 *   currency               — string for display only (₹, $, …)
 */
export default function PricingComputation({
  schemaId, contractId, estimateId, jurisdictionRegionId,
  negotiatedValue, totalValue, currency,
}) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');

  // User-entered fallback when both negotiated and total are blank.
  // Persists in component state for the lifetime of the contract editor.
  const [manualBase, setManualBase]   = useState(null);
  const [askBase, setAskBase]         = useState(false);
  const [draftBase, setDraftBase]     = useState('');

  const negotiated = numOrNull(negotiatedValue);
  const total      = numOrNull(totalValue);
  const effectiveBase =
    negotiated != null && negotiated > 0 ? negotiated
    : total != null && total > 0          ? total
    : manualBase != null && manualBase > 0 ? manualBase
    : 0;
  const baseSource =
    negotiated != null && negotiated > 0 ? 'negotiated'
    : total != null && total > 0          ? 'total'
    : manualBase != null && manualBase > 0 ? 'manual'
    : 'none';

  const fetchResolved = useCallback(async () => {
    if (!schemaId) { setData(null); setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const qs = new URLSearchParams();
      if (contractId)           qs.set('contract_id', contractId);
      if (estimateId)           qs.set('estimate_id', estimateId);
      if (jurisdictionRegionId) qs.set('jurisdiction_region_id', jurisdictionRegionId);
      const r = await apiFetch(`/api/pricing-schemas/${schemaId}/resolve?${qs.toString()}`);
      if (r.ok) { setData(await r.json()); }
      else      { const d = await r.json().catch(() => ({})); setErr(d.error || 'Resolver failed'); }
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [schemaId, contractId, estimateId, jurisdictionRegionId]);

  // Re-resolve whenever schema, contract, jurisdiction, total or negotiated changes.
  useEffect(() => { fetchResolved(); }, [fetchResolved, total, negotiated]);

  // Compute final per-component values + grand total. Memoised so the
  // table re-renders cleanly when manualBase changes without re-fetching.
  const { rows, grandTotal, missingCount, totalTax } = useMemo(() => {
    if (!data) return { rows: [], grandTotal: 0, missingCount: 0, totalTax: 0 };
    const out = [];
    let runningTotal = effectiveBase;
    let totalTaxSum  = 0;
    let missing      = 0;

    for (const c of (data.components || [])) {
      const cat = String(c.category || '').toLowerCase();
      const hasMaster = !c.no_master && (c.rate_pct != null || c.fixed_value != null);

      let computed = 0;
      let displayCategory = cat;
      let noMasterDisplay = false;
      let source = 'none';
      let rateLabel = '—';

      if (cat === 'base') {
        // BASE row gets the effective base value, regardless of master.
        computed = effectiveBase;
        source = 'base';
        rateLabel = '—';
        // runningTotal already includes base; don't double-count below.
      } else if (hasMaster) {
        if (c.rate_pct != null) {
          computed = Math.round(effectiveBase * (Number(c.rate_pct) || 0) / 100 * 100) / 100;
          rateLabel = `${Number(c.rate_pct).toFixed(2)}%`;
        } else {
          computed = Number(c.fixed_value) || 0;
          rateLabel = `${currency || '₹'} ${Number(c.fixed_value).toLocaleString('en-IN')}`;
        }
        source = `master-L${c.match_level}`;
        if (cat === 'tax')               { runningTotal += computed; totalTaxSum += computed; }
        else if (cat === 'markup' ||
                 cat === 'levy')         { runningTotal += computed; }
        else if (cat === 'discount')     { runningTotal -= computed; }
        // round_off is handled below with master too — set its computed as the running total
        if (cat === 'round_off') {
          computed = runningTotal; // ignore master rate, total is the running sum
          source = 'computed';
          rateLabel = '—';
        }
      } else if (cat === 'round_off') {
        // Round-off / total component: just take the running total. No flag.
        computed = runningTotal;
        source = 'computed';
        rateLabel = '—';
      } else {
        // Anything else without a master = red flag, treated as 0%.
        computed = 0;
        noMasterDisplay = true;
        missing += 1;
        rateLabel = '0.00%';
      }

      out.push({
        ...c,
        computed,
        no_master_display: noMasterDisplay,
        _source: source,
        _rateLabel: rateLabel,
        _displayCategory: displayCategory,
      });
    }

    return { rows: out, grandTotal: runningTotal, missingCount: missing, totalTax: totalTaxSum };
  }, [data, effectiveBase, currency]);

  if (!schemaId) return null;
  if (loading)   return <div style={{ color:'#94A3B8', fontSize:12.5 }}>Resolving rates…</div>;
  if (err)       return <div style={{ color:'#EF4444', fontSize:12.5 }}>{err}</div>;
  if (!data)     return null;

  const cur  = currency || '₹';
  const fmt  = (n) => `${cur} ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const ctx = data.context || {};

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      <div style={{ fontSize:12, color:'#94A3B8', lineHeight:1.5 }}>
        Resolved using a <strong>3-level priority lookup</strong> · Base value{' '}
        <strong style={{ color:'#E5E7EB' }}>{fmt(effectiveBase)}</strong>
        {' '}<span style={{ ...pill, background: baseSource === 'negotiated' ? 'rgba(16,185,129,0.18)' : baseSource === 'total' ? 'rgba(59,143,244,0.18)' : baseSource === 'manual' ? 'rgba(245,158,11,0.18)' : 'rgba(239,68,68,0.18)',
          color: baseSource === 'negotiated' ? '#10B981' : baseSource === 'total' ? '#3B8FF4' : baseSource === 'manual' ? '#F59E0B' : '#EF4444' }}>
          {baseSource === 'negotiated' ? 'Negotiated' : baseSource === 'total' ? 'Total' : baseSource === 'manual' ? 'Manual' : 'Not set'}
        </span>
        {' '}· Date <strong style={{ color:'#E5E7EB' }}>{ctx.date || '—'}</strong>
        {ctx.jurisdiction_region_id && <> · Jurisdiction <code style={pill}>L1</code></>}
        {!ctx.jurisdiction_region_id && ctx.company_region_id && <> · Jurisdiction (company) <code style={pill}>L2</code></>}
        {ctx.customer_region_id && <> · Customer region set</>}
      </div>

      {effectiveBase === 0 && (
        <div style={{ background:'rgba(245,158,11,0.10)', border:'1px solid rgba(245,158,11,0.35)',
          color:'#FBBF24', padding:'8px 12px', borderRadius:6, fontSize:12.5,
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
          <span>⚠ No base value yet — fill <strong>Negotiated Value</strong> or <strong>Total Value</strong>, or enter a one-off base below.</span>
          <button onClick={() => { setDraftBase(''); setAskBase(true); }} style={btnInline}>Enter base value</button>
        </div>
      )}

      {missingCount > 0 && (
        <div style={{ background:'rgba(239,68,68,0.10)', border:'1px solid rgba(239,68,68,0.35)',
          color:'#FCA5A5', padding:'8px 12px', borderRadius:6, fontSize:12.5 }}>
          ⚠ {missingCount} component{missingCount !== 1 ? 's' : ''} have no master and were treated as 0%.
        </div>
      )}

      <div style={{ overflowX:'auto', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, minWidth: 0, maxWidth: '100%' }}>
        <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, fontSize:12.5 }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>Component</th>
              <th style={th}>Category</th>
              <th style={{ ...th, textAlign:'right' }}>Rate</th>
              <th style={th}>Match</th>
              <th style={{ ...th, textAlign:'right' }}>Value Applied</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.component_id} style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
                <td style={td}>{r.seq ?? '—'}</td>
                <td style={{ ...td,
                  color: r.no_master_display ? '#EF4444' : '#E5E7EB',
                  fontWeight: r.no_master_display ? 600 : 400 }}>
                  {r.component_name}
                  {r.component_code && <span style={{ marginLeft:6, color:'#64748B' }}>({r.component_code})</span>}
                  {r.no_master_display && (
                    <div style={{ fontSize:11, color:'#F87171', marginTop:2 }}>
                      No masters found for component <strong>{r.component_name}</strong>
                    </div>
                  )}
                </td>
                <td style={{ ...td, color:'#94A3B8' }}>{r._displayCategory || '—'}</td>
                <td style={{ ...td, textAlign:'right', fontFamily:'ui-monospace, monospace',
                  color: r.no_master_display ? '#EF4444' : '#E5E7EB' }}>
                  {r._rateLabel}
                </td>
                <td style={td}>
                  {r.no_master_display
                    ? <span style={{ ...pill, background:'rgba(239,68,68,0.15)', color:'#EF4444' }}>None</span>
                    : (r._source === 'base' || r._source === 'computed'
                        ? <span style={{ ...pill, background:'rgba(148,163,184,0.18)', color:'#94A3B8' }}>—</span>
                        : <span style={{ ...pill, background:'rgba(16,185,129,0.15)', color:'#10B981' }}>L{r.match_level}</span>)}
                </td>
                <td style={{ ...td, textAlign:'right', fontFamily:'ui-monospace, monospace',
                  color: r.no_master_display ? '#EF4444' : '#E5E7EB' }}>
                  {fmt(r.computed)}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop:'2px solid rgba(255,255,255,0.10)', background:'rgba(255,255,255,0.03)' }}>
              <td style={td} colSpan={5}>
                <strong style={{ color:'#E5E7EB' }}>Grand Total (Base + applied rates)</strong>
              </td>
              <td style={{ ...td, textAlign:'right', fontFamily:'ui-monospace, monospace',
                color:'#10B981', fontWeight:700 }}>{fmt(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Modal — manual base entry when negotiated & total are both blank. */}
      {askBase && (
        <div style={modalBackdrop} onClick={() => setAskBase(false)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:14, fontWeight:600, color:'#E5E7EB', marginBottom:8 }}>Enter base value</div>
            <div style={{ fontSize:12, color:'#94A3B8', marginBottom:12 }}>
              Negotiated and Total values are both blank. Enter a one-off base value to apply rates against. This is used for the simulation only — fill in Negotiated or Total on the contract header to make it permanent.
            </div>
            <input type="number" autoFocus value={draftBase} onChange={e => setDraftBase(e.target.value)}
              style={{ width:'100%', padding:'10px 12px', borderRadius:6, border:'1px solid rgba(255,255,255,0.10)',
                background:'rgba(15,23,42,0.6)', color:'#E5E7EB', fontSize:14, outline:'none', marginBottom:14 }}
              placeholder="e.g. 1000000" />
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setAskBase(false)} style={btnGhost}>Cancel</button>
              <button onClick={() => { const n = Number(draftBase); if (Number.isFinite(n) && n > 0) setManualBase(n); setAskBase(false); }} style={btnPrimary}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const th = { padding:'8px 10px', textAlign:'left', background:'rgba(255,255,255,0.04)',
             color:'#94A3B8', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.4px',
             borderBottom:'1px solid rgba(255,255,255,0.10)' };
const td = { padding:'7px 10px', verticalAlign:'middle' };
const pill = { display:'inline-block', padding:'2px 8px', borderRadius:999,
               fontSize:10.5, fontWeight:700, letterSpacing:'0.4px',
               background:'rgba(255,255,255,0.06)', color:'#94A3B8' };

const btnInline  = { padding:'4px 10px', borderRadius:5, fontSize:11.5, fontWeight:600,
                     border:'1px solid rgba(245,158,11,0.45)', background:'rgba(245,158,11,0.18)',
                     color:'#FBBF24', cursor:'pointer' };
const btnPrimary = { padding:'8px 14px', borderRadius:7, fontSize:12.5, fontWeight:600, border:'none',
                     cursor:'pointer', background:'linear-gradient(135deg, #FF00E5, #BF5FFF)', color:'#fff' };
const btnGhost   = { padding:'8px 12px', borderRadius:7, fontSize:12.5, fontWeight:600,
                     border:'1px solid rgba(255,255,255,0.10)', background:'rgba(255,255,255,0.04)',
                     color:'#E5E7EB', cursor:'pointer' };

const modalBackdrop = { position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
                        display:'flex', alignItems:'center', justifyContent:'center', zIndex:999 };
const modalCard     = { width:420, maxWidth:'90vw', background:'#0F172A', border:'1px solid rgba(255,255,255,0.10)',
                        borderRadius:10, padding:18 };
